import React, { useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../services/api';
import { apiOrigin } from '../utils/apiBase';
import { useToast } from '../context/ToastContext';

const RichTextEditor = ({ value, onChange, placeholder }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const quillRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const imageHandler = async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      setUploading(true);

      try {
        const formData = new FormData();
        formData.append('image', file);

        const response = await api.post('/modules/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        const imageUrl = `${apiOrigin()}${response.data.imageUrl}`;

        const quill = quillRef.current.getEditor();
        const range = quill.getSelection();
        quill.insertEmbed(range.index, 'image', imageUrl);
      } catch (error) {
        console.error('Image upload error:', error);
        showToast(t('richText.imageUploadError'), 'error');
      } finally {
        setUploading(false);
      }
    };
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          ['link', 'image'],
          ['clean'],
        ],
        handlers: { image: imageHandler },
      },
    }),
    []
  );

  return (
    <div className="rich-text-editor">
      {/* Suprascriu culorile Quill pentru dark mode — CSS-ul snow e optimizat doar pentru light */}
      <style>{`
        .dark .rich-text-editor .ql-toolbar.ql-snow {
          background: rgb(30 41 59);
          border-color: rgb(51 65 85);
        }
        .dark .rich-text-editor .ql-container.ql-snow {
          background: rgb(15 23 42);
          border-color: rgb(51 65 85);
          color: rgb(226 232 240);
        }
        .dark .rich-text-editor .ql-editor {
          color: rgb(226 232 240);
        }
        .dark .rich-text-editor .ql-editor.ql-blank::before {
          color: rgb(100 116 139);
          font-style: normal;
        }
        .dark .rich-text-editor .ql-snow .ql-stroke {
          stroke: rgb(203 213 225);
        }
        .dark .rich-text-editor .ql-snow .ql-fill,
        .dark .rich-text-editor .ql-snow .ql-stroke.ql-fill {
          fill: rgb(203 213 225);
        }
        .dark .rich-text-editor .ql-snow .ql-picker,
        .dark .rich-text-editor .ql-snow .ql-picker-label {
          color: rgb(203 213 225);
        }
        .dark .rich-text-editor .ql-snow .ql-picker-options {
          background: rgb(30 41 59);
          border-color: rgb(51 65 85);
        }
        .dark .rich-text-editor .ql-snow .ql-tooltip {
          background: rgb(30 41 59);
          border-color: rgb(51 65 85);
          color: rgb(226 232 240);
          box-shadow: 0 0 5px rgba(0,0,0,0.3);
        }
        .dark .rich-text-editor .ql-snow .ql-tooltip input[type=text] {
          background: rgb(15 23 42);
          border-color: rgb(51 65 85);
          color: rgb(226 232 240);
        }
        .dark .rich-text-editor .ql-snow .ql-picker.ql-expanded .ql-picker-label,
        .dark .rich-text-editor .ql-snow .ql-picker-item:hover,
        .dark .rich-text-editor .ql-snow .ql-picker-label:hover,
        .dark .rich-text-editor .ql-snow button:hover,
        .dark .rich-text-editor .ql-snow button.ql-active {
          color: rgb(96 165 250);
        }
        .dark .rich-text-editor .ql-snow button:hover .ql-stroke,
        .dark .rich-text-editor .ql-snow button.ql-active .ql-stroke {
          stroke: rgb(96 165 250);
        }
        .dark .rich-text-editor .ql-snow button:hover .ql-fill,
        .dark .rich-text-editor .ql-snow button.ql-active .ql-fill {
          fill: rgb(96 165 250);
        }
      `}</style>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        placeholder={placeholder || t('richText.placeholder')}
        style={{ minHeight: '300px' }}
      />
      {uploading && (
        <div className="mt-2 text-sm text-blue-600 dark:text-blue-300">
          {t('richText.uploadingImage')}
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
